import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, map, from } from 'rxjs';
import firebase from 'firebase/compat/app';

@Injectable({
  providedIn: 'root'
})
export class OnboardingApiService {

  constructor(
    private fns: AngularFireFunctions,
    private afs: AngularFirestore,
  ) {}

  /** Save org branding — ThemeColor, logo gs:// path, kiosk background gs:// path */
  saveBranding(organizationId: string, branding: {
    themeColor?: string;
    logoLocation?: string;
    kioskBackground?: string;
  }): Observable<{ success: boolean }> {
    const updates: Record<string, any> = {
      UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    if (branding.themeColor) updates['ThemeColor'] = branding.themeColor;
    if (branding.logoLocation) updates['LogoLocation'] = branding.logoLocation;
    if (branding.kioskBackground) updates['KioskBackground'] = branding.kioskBackground;
    return from(
      this.afs.doc(`Organizations/${organizationId}`).update(updates)
    ).pipe(map(() => ({ success: true })));
  }

  /** Get an existing organization by ID — direct Firestore read */
  getOrganization(organizationId: string): Observable<any> {
    return this.afs.doc(`Organizations/${organizationId}`).snapshotChanges().pipe(
      map(snap => {
        if (!snap.payload.exists) {
          throw new Error(`Organization ${organizationId} not found.`);
        }
        return { id: snap.payload.id, ...(snap.payload.data() as any) };
      })
    );
  }

  /** Get business details saved for an organization */
  getBusinessDetails(organizationId: string): Observable<any> {
    return this.afs.doc(`Organizations/${organizationId}`).snapshotChanges().pipe(
      map(snap => {
        if (!snap.payload.exists) {
          return null;
        }
        const data = snap.payload.data() as any;
        // Address can be nested object or flat fields
        const addr = data.Address || {};
        return {
          name: data.Name || '',
          dba: data.DBA || '',
          orgType: data.OrgType || '',
          ein: data.EIN || '',
          phone: data.Phone || '',
          website: data.Website || '',
          disclosure: data.Disclosure || '',
          subDomain: data.SubDomain || '',
          referralCode: data.ReferralCode || '',
          address: {
            line1: addr.line1 || data.StreetAddress1 || '',
            line2: addr.line2 || data.StreetAddress2 || '',
            city: addr.city || data.City || '',
            state: addr.state || data.State || '',
            postal_code: addr.postal_code || data.ZipCode || '',
            country: addr.country || 'US',
          },
        };
      })
    );
  }

  /** Get representative details from main organization document (protected tier, readable by org members) */
  getRepresentativeDetails(organizationId: string): Observable<any> {
    return this.afs.doc(`Organizations/${organizationId}`).snapshotChanges().pipe(
      map(snap => {
        if (!snap.payload.exists) {
          return null;
        }
        const data = snap.payload.data() as any;

        // Parse DOB from ISO string back to day/month/year
        let dobDay = null, dobMonth = null, dobYear = null;
        if (data.RepDOB) {
          const [year, month, day] = data.RepDOB.split('-');
          dobDay = parseInt(day, 10);
          dobMonth = parseInt(month, 10);
          dobYear = parseInt(year, 10);
        }

        const addr = data.RepAddress || {};
        return {
          firstName: data.RepFirstName || '',
          lastName: data.RepLastName || '',
          email: data.RepEmail || '',
          phone: data.RepPhone || '',
          title: data.RepTitle || '',
          dobDay,
          dobMonth,
          dobYear,
          address: {
            line1: addr.line1 || '',
            city: addr.city || '',
            state: addr.state || '',
            postal_code: addr.postal_code || '',
            country: addr.country || 'US',
          },
          ssnLast4: data.RepSSNLast4 || '',
        };
      })
    );
  }

  /** Save business details to Firestore — direct write (org admin, field-allowlisted by rules) */
  saveBusinessDetails(organizationId: string, details: any): Observable<{ success: boolean }> {
    const addr = details.address || {};
    const subDomainSlug = (details.subDomain || '').toLowerCase()
      .replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-');
    const data: Record<string, any> = {
      Name: details.name || '',
      DBA: details.dba || '',
      OrgType: details.orgType || '',
      EIN: details.ein || '',
      Phone: details.phone || '',
      Website: details.website || '',
      Disclosure: details.disclosure ||
        'Your contribution is tax-deductible to the extent allowed by law. ' +
        'No goods or services were provided in exchange for this donation.',
      SubDomain: subDomainSlug,
      SubDomainUrl: subDomainSlug ? `https://${subDomainSlug}.chareety.com` : '',
      Address: addr,
      StreetAddress1: addr.line1 || '',
      StreetAddress2: addr.line2 || '',
      City: addr.city || '',
      State: addr.state || '',
      ZipCode: addr.postal_code || '',
      OnboardingBusinessDetailsComplete: true,
      UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    return from(
      this.afs.doc(`Organizations/${organizationId}`).update(data)
    ).pipe(map(() => ({ success: true })));
  }

  /** Save representative details to Firestore — direct write (org admin, field-allowlisted by rules) */
  saveRepresentativeDetails(organizationId: string, details: any): Observable<{ success: boolean }> {
    const dob = `${details.dobYear}-` +
      `${String(details.dobMonth).padStart(2, '0')}-` +
      `${String(details.dobDay).padStart(2, '0')}`;
    const data: Record<string, any> = {
      RepFirstName: details.firstName || '',
      RepLastName: details.lastName || '',
      RepEmail: details.email || '',
      RepPhone: details.phone || '',
      RepTitle: details.title || '',
      RepDOB: dob,
      RepAddress: details.address || {},
      RepSSNLast4: details.ssnLast4 || '',
      RepresentativeName: `${details.firstName} ${details.lastName}`.trim(),
      OnboardingRepresentativeComplete: true,
      UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    return from(
      this.afs.doc(`Organizations/${organizationId}`).update(data)
    ).pipe(map(() => ({ success: true })));
  }

  /** Create a Stripe Connect Express account for the organization */
  createStripeConnectAccount(
    organizationId: string,
    email: string,
    businessName: string
  ): Observable<{ accountId: string }> {
    return this.fns.httpsCallable('createStripeConnectAccount')({
      organizationId,
      email,
      businessName,
    });
  }

  /** Get the hosted Stripe onboarding link */
  getStripeOnboardingLink(
    organizationId: string,
    returnUrl: string,
    refreshUrl: string
  ): Observable<{ url: string }> {
    return this.fns.httpsCallable('getStripeOnboardingLink')({
      organizationId,
      returnUrl,
      refreshUrl,
    });
  }

  /** Check current Stripe account verification status */
  getStripeAccountStatus(organizationId: string): Observable<{
    status: string;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    requirements: any;
  }> {
    return this.fns.httpsCallable('getStripeAccountStatus')({ organizationId });
  }

  /** Save and validate Brevo API configuration — direct Firestore write */
  /** Save Brevo config via Cloud Function (handles PrivateData write, org update, spoke replication) */
  saveBrevoConfig(
    organizationId: string,
    apiKey: string,
    senderName: string,
    senderEmail: string
  ): Observable<{ success: boolean; accountEmail: string }> {
    return this.fns.httpsCallable('saveBrevoConfig')({
      organizationId,
      apiKey,
      senderName,
      senderEmail,
    });
  }

  /**
   * Records document upload completion — direct write (org admin, field-allowlisted by rules).
   * URLs must reference Firebase Storage (validated client-side before upload).
   */
  markDocumentsComplete(
    organizationId: string,
    documentUrls: string[]
  ): Observable<{ success: boolean; count: number }> {
    const urls = Array.isArray(documentUrls) ? documentUrls : [];
    const data: Record<string, any> = {
      DocumentUrls: urls,
      OnboardingDocumentsComplete: urls.length > 0,
      DocumentsUploadedAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    return from(
      this.afs.doc(`Organizations/${organizationId}`).update(data)
    ).pipe(map(() => ({ success: true, count: urls.length })));
  }

  /** Send the onboarding completion congratulations email via Brevo */
  sendOnboardingCompleteEmail(
    organizationId: string
  ): Observable<{ success: boolean; messageId?: string }> {
    return this.fns.httpsCallable('sendOnboardingCompleteEmail')({
      organizationId,
    });
  }

  /**
   * Creates a Stripe Customer and SetupIntent for ACH Direct Debit billing.
   * Returns the SetupIntent client_secret so the front-end can open Financial Connections.
   */
  setupOrgBilling(
    organizationId: string
  ): Observable<{ clientSecret: string; setupIntentId: string; customerId: string }> {
    return this.fns.httpsCallable('manageOrgBilling')({ action: 'setup', organizationId });
  }

  /**
   * After the user links a bank account via Stripe Financial Connections,
   * retrieves the confirmed SetupIntent and saves the payment method to Firestore.
   */
  confirmOrgBillingAccount(
    organizationId: string,
    setupIntentId: string
  ): Observable<{ success: boolean; bankName: string; bankLast4: string; customerId: string }> {
    return this.fns.httpsCallable('manageOrgBilling')({ action: 'confirm', organizationId, setupIntentId });
  }

  /**
   * Returns current month's usage counts and a projected billing breakdown.
   */
  getBillingUsage(
    organizationId: string
  ): Observable<{
    usage: { offlineCount: number; commsCount: number; simDevices: number };
    costs: {
      offlineChargeCents: number;
      commsChargeCents: number;
      simChargeCents: number;
      total: number;
    };
    rates: {
      offlinePerRecord: number;
      commsPerUnit: number;
      simPerDevice: number;
      offlineFree: number;
      commsFree: number;
    };
    bankLast4: string;
    bankName: string;
    billingActive: boolean;
    periodStart: unknown;
  }> {
    return this.fns.httpsCallable('getBillingUsage')({ organizationId });
  }

  /**
   * Marks onboarding as fully complete and queues spoke project provisioning.
   * Prerequisites checked server-side: Stripe must be active, Brevo must be configured.
   */
  initiateSpokeProvisioning(
    organizationId: string
  ): Observable<{ success: boolean; status: string }> {
    return this.fns.httpsCallable('initiateSpokeProvisioning')({ organizationId });
  }

  /** Check if a subdomain slug is available */
  checkSubDomainAvailability(slug: string): Observable<{ available: boolean; slug: string }> {
    return this.fns.httpsCallable('checkSubDomainAvailability')({ slug });
  }

  /** Get the full onboarding completion status */
  getOnboardingStatus(organizationId: string): Observable<{
    organizationId: string;
    name: string;
    email: string;
    steps: {
      businessDetails: boolean;
      representativeDetails: boolean;
      documents: boolean;
      stripeConnect: boolean;
      brevo: boolean;
    };
    stripeAccountId: string | null;
    stripeStatus: string;
    brevoConfigured: boolean;
    provisioningStatus: string | null;
    isComplete: boolean;
  }> {
    return this.fns.httpsCallable('getOnboardingStatus')({ organizationId });
  }

  /**
   * Verify organization auth token.
   * Called when user clicks the onboarding link with ?orgId=X&token=T
   * Returns a session token for the browser to store.
   */
  verifyOrgAuth(organizationId: string, token: string): Observable<{
    success: boolean;
  }> {
    return this.fns.httpsCallable('verifyOrgAuth')({
      organizationId,
      token,
    });
  }

  /**
   * Resend org verification email.
   * Called when org wants to re-verify or needs a new verification link.
   * Can be called with orgId or email to find the org.
   */
  resendOrgVerification(organizationId: string): Observable<{
    success: boolean;
    messageId: string;
  }> {
    return this.fns.httpsCallable('resendOrgVerification')({
      organizationId,
    });
  }

  /**
   * Send onboarding invite email.
   * Called by Partners app after creating an organization for a referral.
   * Generates auth token and sends invite email with onboarding link.
   */
  sendOnboardingInvite(
    organizationId: string,
    leadEmail: string,
    leadName?: string,
    partnerName?: string
  ): Observable<{
    success: boolean;
    messageId: string;
    onboardingUrl: string;
  }> {
    return this.fns.httpsCallable('sendOnboardingInvite')({
      organizationId,
      leadEmail,
      leadName: leadName || '',
      partnerName: partnerName || '',
    });
  }

  /**
   * Create organization with authenticated user association.
   * Called during sign-up to create org + associate user as admin.
   * @param orgName Organization name
   * @param email Admin email (primary contact)
   * @param uid Firebase Auth UID of the user
   * @returns organizationId and initialization token
   */
  createOrganizationWithAuth(
    orgName: string,
    email: string,
    uid: string,
    legalAcceptance?: any,
    referralInfo?: { referralId?: string; partnerId?: string; referralCode?: string }
  ): Observable<{
    success: boolean;
    organizationId: string;
    adminUserEmail: string;
  }> {
    return this.fns.httpsCallable('createOrganizationWithAuth')({
      orgName,
      email,
      uid,
      legalAcceptance,
      ...(referralInfo || {}),
    });
  }

  /**
   * Get all organizations where the user is an admin.
   * @param uid Firebase Auth UID
   * @returns Array of organizations
   */
  getUserOrganizations(uid: string): Observable<any[]> {
    return this.fns.httpsCallable('getUserOrganizations')({ uid });
  }

  /**
   * Create device order payment SetupIntent (card or bank)
   * @param organizationId Organization ID
   * @param paymentData Payment type, cart, and billing preferences
   * @returns clientSecret and setupIntentId
   */
  createDeviceOrderPayment(
    organizationId: string,
    paymentData: {
      paymentType: 'card' | 'bank';
      cart: any;
      useForBilling: boolean;
    }
  ): Observable<{
    clientSecret: string;
    setupIntentId: string;
    customerId: string;
  }> {
    return this.fns.httpsCallable('manageDeviceOrder')({
      action: 'create',
      organizationId,
      ...paymentData,
    });
  }

  /**
   * Confirm device order after payment succeeds
   * Charges payment method, creates order, upgrades to premium
   */
  confirmDeviceOrder(
    organizationId: string,
    confirmData: {
      setupIntentId: string;
      cart: any;
      useForBilling: boolean;
    }
  ): Observable<{
    orderId: string;
    totalCharged: number;
    deviceCount: number;
  }> {
    return this.fns.httpsCallable('manageDeviceOrder')({
      action: 'confirm',
      organizationId,
      ...confirmData,
    });
  }

  /**
   * Mark device as installed (called by sales partner)
   */
  markDeviceInstalled(
    organizationId: string,
    deviceId: string
  ): Observable<{ success: boolean }> {
    return this.fns.httpsCallable('markDeviceInstalled')({
      organizationId,
      deviceId,
    });
  }

  /**
   * Get all device orders for an organization
   */
  getDeviceOrders(organizationId: string): Observable<any[]> {
    return this.afs
      .collection('Organizations')
      .doc(organizationId)
      .collection('DeviceOrders')
      .snapshotChanges()
      .pipe(
        map(snapshot =>
          snapshot.map(doc => ({
            id: doc.payload.doc.id,
            ...(doc.payload.doc.data() as any),
          }))
        )
      );
  }
}
